import React, { useState, useEffect, useCallback } from 'react';
import { supabase } from './supabaseClient';
import { useCompany } from './CompanyContext';
import { useImpersonation } from './ImpersonationContext';
import { SGA_CLASSES } from './Products';
import jsPDF from 'jspdf';
import * as XLSX from 'xlsx';
import { useComplianceScore, type ComplianceScoreResult } from './lib/complianceScore';
import { nivelProductos } from './lib/compatibilidad';
import logoSrc from './assets/storsafe-logo.png';
import './Reports.css';

// ── Types ─────────────────────────────────────────────────────────────────────

interface Product {
  id: string;
  name: string;
  cas_number: string | null;
  provider: string | null;
  sga_classes: string[];
  sds_url: string | null;
}

interface Warehouse {
  id: string;
  name: string;
  description: string | null;
}

interface Zone {
  id: string;
  warehouse_id: string;
  name: string;
  product_ids: string[];
}

interface ReportData {
  products: Product[];
  warehouses: Warehouse[];
  zones: Zone[];
}

interface HistoryEntry {
  id: string;
  period: string;
  company_id: string;
  company_name: string;
  generated_at: string;
}

// ── Compatibilidad: tabla única (src/lib/compatibilidad.ts) ─────────────────
// Aquí el nivel 'precaucion' se sigue llamando 'segregar' internamente.

function productCompat(a: Product, b: Product): 'incompatible' | 'segregar' | 'compatible' {
  const nivel = nivelProductos(a, b);
  return nivel === 'precaucion' ? 'segregar' : nivel;
}

function zoneHasAlert(zone: Zone, productMap: Map<string, Product>): boolean {
  const prods = zone.product_ids.map(id => productMap.get(id)).filter(Boolean) as Product[];
  for (let i = 0; i < prods.length; i++)
    for (let j = i + 1; j < prods.length; j++)
      if (productCompat(prods[i], prods[j]) !== 'compatible') return true;
  return false;
}

function zoneHasIncompat(zone: Zone, productMap: Map<string, Product>): boolean {
  const prods = zone.product_ids.map(id => productMap.get(id)).filter(Boolean) as Product[];
  for (let i = 0; i < prods.length; i++)
    for (let j = i + 1; j < prods.length; j++)
      if (productCompat(prods[i], prods[j]) === 'incompatible') return true;
  return false;
}

// ── Constants ─────────────────────────────────────────────────────────────────

const MONTHS = [
  'Enero','Febrero','Marzo','Abril','Mayo','Junio',
  'Julio','Agosto','Septiembre','Octubre','Noviembre','Diciembre',
];

// ── PDF Generation ────────────────────────────────────────────────────────────

interface PdfArgs {
  companyName: string;
  period: string;
  data: ReportData;
  productMap: Map<string, Product>;
  incompatiblePairs: { a: Product; b: Product }[];
  segregarPairs: { a: Product; b: Product }[];
  zonesWithAlerts: Zone[];
  byClass: Record<string, Product[]>;
  logoImg: HTMLImageElement | null;
}

function generatePdf(args: PdfArgs) {
  const { companyName, period, data, productMap, incompatiblePairs, segregarPairs, zonesWithAlerts, byClass, logoImg } = args;
  const pdf  = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
  const pw   = pdf.internal.pageSize.getWidth();
  const ph   = pdf.internal.pageSize.getHeight();
  const M    = 14;
  const UW   = pw - 2 * M;
  const FTR  = 18;
  let   y    = 0;

  // ── Helpers ────────────────────────────────────────────────────────────────

  const fill   = (r: number, g: number, b: number) => pdf.setFillColor(r, g, b);
  const stroke = (r: number, g: number, b: number) => pdf.setDrawColor(r, g, b);
  const color  = (r: number, g: number, b: number) => pdf.setTextColor(r, g, b);
  const font   = (style: 'normal'|'bold'|'italic', size: number) => { pdf.setFont('helvetica', style); pdf.setFontSize(size); };
  const rect   = (x: number, ry: number, w: number, h: number) => pdf.rect(x, ry, w, h, 'F');
  const rrect  = (x: number, ry: number, w: number, h: number, r: number) => pdf.roundedRect(x, ry, w, h, r, r, 'F');
  const txt    = (s: string, x: number, ty: number, opts?: object) => pdf.text(s, x, ty, opts ?? {});
  const line   = (x1: number, y1: number, x2: number, y2: number) => pdf.line(x1, y1, x2, y2);

  const addFooter = (pageNum: number) => {
    fill(15, 23, 42); rect(0, ph - FTR, pw, FTR);
    fill(34, 197, 94); rect(0, ph - FTR, pw, 0.5);
    font('italic', 5.5); color(71, 85, 105);
    txt('AVISO LEGAL: StorSafe es una herramienta de apoyo. La informacion es responsabilidad exclusiva del usuario.', M, ph - 12.5);
    txt('No reemplaza asesoria profesional en seguridad quimica. StorSafe no asume responsabilidad por danos directos, indirectos o lucro cesante.', M, ph - 8);
    font('normal', 7); color(100, 116, 139);
    txt('Generado por StorSafe · Sistema de Gestion de Bodegas Quimicas', M, ph - 3.5);
    txt(`Pag. ${pageNum}`, pw - M, ph - 3.5, { align: 'right' });
  };

  const contHeader = (subtitle: string) => {
    fill(15, 23, 42); rect(0, 0, pw, 12);
    fill(34, 197, 94); rect(0, 11, pw, 1);
    font('bold', 7); color(34, 197, 94); txt('StorSafe', M, 8);
    font('normal', 7); color(100, 116, 139); txt(`· Reporte de Cumplimiento · ${companyName} · ${period}`, M + 14, 8);
    font('italic', 7); color(71, 85, 105); txt(subtitle, pw - M, 8, { align: 'right' });
  };

  let pageNum = 1;

  const checkPage = (needed: number, subtitle: string) => {
    if (y + needed > ph - FTR - 4) {
      addFooter(pageNum++);
      pdf.addPage();
      contHeader(subtitle);
      y = 18;
    }
  };

  const section = (num: string, title: string, r: number, g: number, b: number) => {
    checkPage(14, title);
    fill(22, 33, 51); rect(M, y, UW, 9);
    fill(r, g, b); rect(M, y, 3.5, 9);
    font('bold', 8); color(226, 232, 240);
    txt(`${num}  ${title}`, M + 7, y + 6.3);
    y += 14;
  };

  const statCard = (label: string, value: string, x: number, w: number, r: number, g: number, b: number) => {
    fill(22, 33, 51); rrect(x, y, w, 20, 2);
    fill(r, g, b);    rrect(x, y, w, 2.5, 1);
    font('bold', 16); color(r, g, b); txt(value, x + w / 2, y + 13, { align: 'center' });
    font('normal', 7); color(148, 163, 184); txt(label, x + w / 2, y + 18.5, { align: 'center' });
  };

  // ── COVER PAGE ────────────────────────────────────────────────────────────

  fill(15, 23, 42); rect(0, 0, pw, ph);
  fill(34, 197, 94); rect(0, 0, pw, 1.5);
  fill(34, 197, 94); rect(0, ph - 1.5, pw, 1.5);

  // Logo
  if (logoImg) {
    const lw = 70; const lh = lw * (logoImg.naturalHeight / logoImg.naturalWidth);
    pdf.addImage(logoImg, 'PNG', pw / 2 - lw / 2, 32, lw, lh);
  } else {
    fill(29, 111, 163); rrect(pw / 2 - 18, 38, 36, 36, 5);
    font('bold', 20); color(255, 255, 255); txt('SS', pw / 2, 60, { align: 'center' });
  }

  // Title
  font('bold', 24); color(241, 245, 249);
  txt('REPORTE DE', pw / 2, 90, { align: 'center' });
  txt('CUMPLIMIENTO', pw / 2, 100, { align: 'center' });

  // Green underline
  fill(34, 197, 94); rect(pw / 2 - 30, 104, 60, 1);

  // Subtitle
  font('normal', 10); color(100, 116, 139);
  txt('Sistema de Gestión de Bodegas Químicas', pw / 2, 113, { align: 'center' });

  // Info box
  fill(22, 33, 51); rrect(pw / 2 - 55, 125, 110, 55, 3);
  fill(34, 197, 94); rect(pw / 2 - 55, 125, 3.5, 55);

  font('normal', 8); color(100, 116, 139); txt('EMPRESA', pw / 2 - 47, 136);
  font('bold', 12); color(241, 245, 249);
  const compLines = pdf.splitTextToSize(companyName, 95);
  txt(compLines[0] ?? companyName, pw / 2 - 47, 145);

  font('normal', 8); color(100, 116, 139); txt('PERÍODO', pw / 2 - 47, 157);
  font('bold', 11); color(34, 197, 94); txt(period, pw / 2 - 47, 166);

  // Generation date
  const genDate = new Date().toLocaleDateString('es-EC', { weekday:'long', year:'numeric', month:'long', day:'numeric' });
  font('normal', 8); color(100, 116, 139);
  txt('Fecha de generación:', pw / 2, 192, { align: 'center' });
  font('normal', 8); color(148, 163, 184);
  txt(genDate, pw / 2, 199, { align: 'center' });

  // Footer cover
  fill(22, 33, 51); rect(0, ph - 28, pw, 28);
  fill(34, 197, 94); rect(0, ph - 28, pw, 0.5);
  font('bold', 9); color(34, 197, 94); txt('StorSafe', pw / 2, ph - 17, { align: 'center' });
  font('normal', 7); color(100, 116, 139);
  txt('Sistema de Gestión de Bodegas Químicas · Cumplimiento Normativo', pw / 2, ph - 10, { align: 'center' });
  txt(`© ${new Date().getFullYear()} StorSafe · Todos los derechos reservados`, pw / 2, ph - 4, { align: 'center' });

  // ── PAGE 2: EXECUTIVE SUMMARY ─────────────────────────────────────────────

  pdf.addPage();
  pageNum = 2;
  contHeader('Resumen Ejecutivo');
  y = 18;

  section('1.', 'RESUMEN EJECUTIVO', 34, 197, 94);

  const CW = (UW - 6) / 4;
  statCard('Total Productos',   String(data.products.length),   M,              CW, 34,  197, 94);
  statCard('Bodegas Activas',   String(data.warehouses.length), M + CW + 2,     CW, 59,  130, 246);
  statCard('Zonas Registradas', String(data.zones.length),      M + (CW + 2)*2, CW, 168, 85,  247);
  statCard('Alertas Activas',   String(zonesWithAlerts.length), M + (CW + 2)*3, CW, 239, 68,  68);
  y += 26;

  // Compliance indicators
  checkPage(40, 'Resumen Ejecutivo');
  fill(22, 33, 51); rect(M, y, UW, 30);
  fill(34, 197, 94); rect(M, y, 3.5, 30);

  font('bold', 8); color(226, 232, 240); txt('Indicadores de compatibilidad química', M + 8, y + 7);

  const sdsOk  = data.products.filter(p => p.sds_url).length;
  const sdsPct = data.products.length ? Math.round((sdsOk / data.products.length) * 100) : 0;
  const zoneAlertPct = data.zones.length ? Math.round((zonesWithAlerts.length / data.zones.length) * 100) : 0;
  const incompatPct  = data.products.length > 1
    ? Math.round((incompatiblePairs.length / ((data.products.length * (data.products.length - 1)) / 2)) * 100)
    : 0;

  const indicators = [
    { label: 'Hojas SDS disponibles',        value: `${sdsOk}/${data.products.length}`,   pct: sdsPct,       color: sdsPct >= 80 ? [34,197,94] : sdsPct >= 50 ? [245,158,11] : [239,68,68] },
    { label: 'Pares incompatibles detectados', value: String(incompatiblePairs.length),    pct: 100 - incompatPct, color: incompatiblePairs.length === 0 ? [34,197,94] : [239,68,68] },
    { label: 'Zonas sin alertas',             value: `${data.zones.length - zonesWithAlerts.length}/${data.zones.length}`, pct: 100 - zoneAlertPct, color: zonesWithAlerts.length === 0 ? [34,197,94] : [239,68,68] },
    { label: 'Pares a segregar',              value: String(segregarPairs.length),         pct: 100, color: segregarPairs.length === 0 ? [34,197,94] : [245,158,11] },
  ];

  indicators.forEach((ind, i) => {
    const ix = M + 8 + (i * ((UW - 8) / 4));
    font('normal', 7); color(148, 163, 184); txt(ind.label, ix, y + 14);
    font('bold', 9); color(ind.color[0], ind.color[1], ind.color[2]); txt(ind.value, ix, y + 21);
  });

  y += 36;

  // ── PAGE 3: PRODUCT INVENTORY ─────────────────────────────────────────────

  section('2.', 'INVENTARIO DE PRODUCTOS POR CLASE SGA', 59, 130, 246);

  // Header row
  const colX = [M, M+7, M+77, M+112, M+147];

  const tableHeader = () => {
    fill(22, 33, 51); rect(M, y, UW, 8);
    stroke(51, 65, 85); pdf.setDrawColor(51, 65, 85);
    font('bold', 7); color(100, 116, 139);
    ['#','Nombre del Producto','N° CAS','Proveedor','Hoja SDS'].forEach((h, i) => {
      txt(h, colX[i] + 1.5, y + 5.5);
    });
    y += 8;
  };

  const SGA_ORDERED = SGA_CLASSES.filter(c => byClass[c.id]?.length > 0);

  for (const cls of SGA_ORDERED) {
    const clsProds = byClass[cls.id] ?? [];
    checkPage(20 + clsProds.length * 7, 'Inventario de Productos');

    // Class header
    fill(22, 33, 51); rect(M, y, UW, 8);
    const clsColor = hexToRgb(cls.color);
    fill(clsColor[0], clsColor[1], clsColor[2]); rect(M, y, 3.5, 8);
    font('bold', 8); color(226, 232, 240);
    txt(`${cls.label}  (${clsProds.length} producto${clsProds.length !== 1 ? 's' : ''})`, M + 7, y + 5.5);
    y += 8;

    tableHeader();

    for (let idx = 0; idx < clsProds.length; idx++) {
      const p = clsProds[idx];
      checkPage(7, 'Inventario de Productos');
      const rowBg = idx % 2 === 0 ? [15,23,42] : [18,27,46];
      fill(rowBg[0], rowBg[1], rowBg[2]); rect(M, y, UW, 7);
      font('normal', 7); color(203, 213, 225);
      txt(String(idx + 1), colX[0] + 1.5, y + 5);
      txt(truncate(p.name, 32), colX[1] + 1.5, y + 5);
      txt(p.cas_number ?? '—', colX[2] + 1.5, y + 5);
      txt(truncate(p.provider ?? '—', 16), colX[3] + 1.5, y + 5);

      const hasSds = !!p.sds_url;
      fill(hasSds ? 34 : 239, hasSds ? 197 : 68, hasSds ? 94 : 68);
      rrect(colX[4] + 1.5, y + 1.5, 22, 4, 1);
      font('bold', 6); color(255, 255, 255);
      txt(hasSds ? 'Disponible' : 'Faltante', colX[4] + 12.5, y + 4.5, { align: 'center' });
      y += 7;
    }
    y += 4;
  }

  // ── SECTION 3: SDS STATUS ─────────────────────────────────────────────────

  section('3.', 'ESTADO DE HOJAS DE SEGURIDAD (SDS)', 245, 158, 11);

  const withSds    = data.products.filter(p => p.sds_url);
  const missingSds = data.products.filter(p => !p.sds_url);

  // Summary stat
  checkPage(16, 'Estado SDS');
  const sdsBarW = UW * (sdsOk / Math.max(data.products.length, 1));
  fill(22, 33, 51); rect(M, y, UW, 10);
  fill(34, 197, 94); rect(M, y, sdsBarW, 10);
  font('bold', 8); color(255, 255, 255);
  txt(`${sdsPct}% de hojas SDS disponibles (${sdsOk} de ${data.products.length})`, M + 4, y + 6.8);
  y += 14;

  // Missing SDS list
  if (missingSds.length > 0) {
    checkPage(14 + Math.min(missingSds.length, 20) * 7, 'Estado SDS');
    fill(239, 68, 68); rect(M, y, 3.5, 8);
    fill(40, 25, 25); rect(M + 3.5, y, UW - 3.5, 8);
    font('bold', 8); color(252, 165, 165);
    txt(`Productos SIN hoja de seguridad (${missingSds.length})`, M + 7, y + 5.5);
    y += 8;

    missingSds.forEach((p, idx) => {
      checkPage(6, 'Estado SDS');
      fill(idx % 2 === 0 ? 30 : 25, idx % 2 === 0 ? 15 : 12, idx % 2 === 0 ? 15 : 12);
      rect(M, y, UW, 6);
      fill(239, 68, 68); rect(M + 3.5, y + 1.5, 2, 3);
      font('normal', 7); color(203, 213, 225);
      txt(truncate(p.name, 50), M + 9, y + 4.5);
      font('normal', 6.5); color(100, 116, 139);
      txt(p.cas_number ?? '', M + 110, y + 4.5);
      y += 6;
    });
    y += 4;
  }

  if (withSds.length > 0) {
    checkPage(14, 'Estado SDS');
    fill(34, 197, 94); rect(M, y, 3.5, 8);
    fill(15, 30, 20); rect(M + 3.5, y, UW - 3.5, 8);
    font('bold', 8); color(134, 239, 172);
    txt(`Productos CON hoja de seguridad (${withSds.length})`, M + 7, y + 5.5);
    y += 8;

    withSds.forEach((p, idx) => {
      checkPage(6, 'Estado SDS');
      fill(idx % 2 === 0 ? 15 : 17, idx % 2 === 0 ? 30 : 25, idx % 2 === 0 ? 20 : 18);
      rect(M, y, UW, 6);
      fill(34, 197, 94); rect(M + 3.5, y + 1.5, 2, 3);
      font('normal', 7); color(203, 213, 225);
      txt(truncate(p.name, 50), M + 9, y + 4.5);
      font('normal', 6.5); color(100, 116, 139);
      txt(p.cas_number ?? '', M + 110, y + 4.5);
      y += 6;
    });
    y += 4;
  }

  // ── SECTION 4: COMPATIBILITY SUMMARY ─────────────────────────────────────

  section('4.', 'MATRIZ DE COMPATIBILIDAD — PARES CRÍTICOS', 239, 68, 68);

  if (incompatiblePairs.length === 0 && segregarPairs.length === 0) {
    checkPage(14, 'Compatibilidad');
    fill(15, 30, 20); rect(M, y, UW, 12);
    fill(34, 197, 94); rect(M, y, 3.5, 12);
    font('bold', 9); color(134, 239, 172);
    txt('No se detectaron incompatibilidades entre los productos registrados.', M + 8, y + 7.5);
    y += 16;
  }

  if (incompatiblePairs.length > 0) {
    checkPage(14, 'Compatibilidad');
    fill(40, 20, 20); rect(M, y, UW, 8);
    fill(239, 68, 68); rect(M, y, 3.5, 8);
    font('bold', 8); color(252, 165, 165);
    txt(`INCOMPATIBLES — No deben almacenarse juntos (${incompatiblePairs.length} par${incompatiblePairs.length !== 1 ? 'es' : ''})`, M + 7, y + 5.5);
    y += 8;

    incompatiblePairs.forEach((pair, idx) => {
      checkPage(7, 'Compatibilidad');
      fill(idx % 2 === 0 ? 35 : 30, idx % 2 === 0 ? 15 : 12, idx % 2 === 0 ? 15 : 12);
      rect(M, y, UW, 7);
      fill(239, 68, 68); rrect(M + 2, y + 1.5, 3, 4, 0.5);
      font('bold', 7); color(252, 165, 165);
      txt(truncate(pair.a.name, 30), M + 8, y + 5);
      font('normal', 7); color(100, 116, 139);
      txt('vs', M + 85, y + 5);
      font('bold', 7); color(252, 165, 165);
      txt(truncate(pair.b.name, 30), M + 92, y + 5);
      // Classes
      font('normal', 6); color(100, 116, 139);
      const aClasses = pair.a.sga_classes.join(', ');
      txt(aClasses, M + 8, y + 6.5);
      y += 7;
    });
    y += 4;
  }

  if (segregarPairs.length > 0) {
    checkPage(14, 'Compatibilidad');
    fill(35, 28, 10); rect(M, y, UW, 8);
    fill(245, 158, 11); rect(M, y, 3.5, 8);
    font('bold', 8); color(253, 230, 138);
    txt(`A SEGREGAR — Requieren separación física (${segregarPairs.length} par${segregarPairs.length !== 1 ? 'es' : ''})`, M + 7, y + 5.5);
    y += 8;

    segregarPairs.forEach((pair, idx) => {
      checkPage(7, 'Compatibilidad');
      fill(idx % 2 === 0 ? 30 : 25, idx % 2 === 0 ? 22 : 18, idx % 2 === 0 ? 10 : 8);
      rect(M, y, UW, 7);
      fill(245, 158, 11); rrect(M + 2, y + 1.5, 3, 4, 0.5);
      font('bold', 7); color(253, 230, 138);
      txt(truncate(pair.a.name, 30), M + 8, y + 5);
      font('normal', 7); color(100, 116, 139);
      txt('vs', M + 85, y + 5);
      font('bold', 7); color(253, 230, 138);
      txt(truncate(pair.b.name, 30), M + 92, y + 5);
      y += 7;
    });
    y += 4;
  }

  // ── SECTION 5: WAREHOUSE STATUS ───────────────────────────────────────────

  section('5.', 'ESTADO DE BODEGAS Y ZONAS', 168, 85, 247);

  for (const wh of data.warehouses) {
    const whZones = data.zones.filter(z => z.warehouse_id === wh.id);
    const alertCount = whZones.filter(z => zoneHasAlert(z, productMap)).length;

    checkPage(12 + whZones.length * 7, 'Bodegas y Zonas');

    // Warehouse row
    fill(22, 33, 51); rect(M, y, UW, 9);
    fill(59, 130, 246); rect(M, y, 3.5, 9);
    font('bold', 8); color(226, 232, 240);
    txt(wh.name, M + 7, y + 6);
    if (wh.description) {
      font('normal', 7); color(100, 116, 139);
      txt(truncate(wh.description, 60), M + 60, y + 6);
    }
    const alertBgR = alertCount > 0 ? [239,68,68] : [34,197,94];
    fill(alertBgR[0], alertBgR[1], alertBgR[2]);
    rrect(pw - M - 35, y + 1.5, 34, 6, 1);
    font('bold', 7); color(255, 255, 255);
    txt(alertCount > 0 ? `${alertCount} alerta${alertCount !== 1 ? 's' : ''}` : 'Sin alertas', pw - M - 18, y + 5.5, { align: 'center' });
    y += 9;

    if (whZones.length === 0) {
      font('italic', 7); color(71, 85, 105);
      txt('   Sin zonas registradas', M + 6, y + 5);
      y += 7;
    } else {
      for (let idx = 0; idx < whZones.length; idx++) {
        const zone = whZones[idx];
        checkPage(7, 'Bodegas y Zonas');
        fill(idx % 2 === 0 ? 17 : 15, idx % 2 === 0 ? 25 : 23, idx % 2 === 0 ? 40 : 38);
        rect(M, y, UW, 7);
        const hasAlert = zoneHasAlert(zone, productMap);
        fill(hasAlert ? 239 : 51, hasAlert ? 68 : 65, hasAlert ? 68 : 85);
        rect(M + 4, y, 1, 7);
        font('normal', 7); color(203, 213, 225);
        txt(zone.name, M + 8, y + 5);
        font('normal', 6.5); color(100, 116, 139);
        txt(`${zone.product_ids.length} producto${zone.product_ids.length !== 1 ? 's' : ''}`, M + 80, y + 5);
        if (hasAlert) {
          // Solo pares de precaución: no se rotula como incompatibilidad.
          const hasIncompat = zoneHasIncompat(zone, productMap);
          font('bold', 6.5);
          if (hasIncompat) color(252, 165, 165); else color(252, 211, 77);
          txt(hasIncompat ? 'INCOMPATIBILIDAD DETECTADA' : 'REQUIERE SEGREGACIÓN', pw - M - 4, y + 5, { align: 'right' });
        }
        y += 7;
      }
    }
    y += 4;
  }

  if (data.warehouses.length === 0) {
    checkPage(12, 'Bodegas y Zonas');
    fill(22, 33, 51); rect(M, y, UW, 12);
    font('italic', 8); color(71, 85, 105);
    txt('No hay bodegas registradas en este período.', M + 6, y + 8);
    y += 16;
  }

  // ── SIGNATURE PAGE ────────────────────────────────────────────────────────

  addFooter(pageNum++);
  pdf.addPage();
  contHeader('Firma y Sello');
  y = 18;

  // Signature section
  section('6.', 'FIRMA Y SELLO DE VALIDACIÓN', 34, 197, 94);

  checkPage(80, 'Firma');
  fill(22, 33, 51); rrect(M, y, UW, 70, 3);
  fill(34, 197, 94); rect(M, y, 3.5, 70);

  font('bold', 9); color(226, 232, 240);
  txt('Validación del Reporte', M + 10, y + 12);

  font('normal', 8); color(148, 163, 184);
  txt('Empresa:', M + 10, y + 22);
  font('bold', 9); color(241, 245, 249);
  txt(companyName, M + 35, y + 22);

  font('normal', 8); color(148, 163, 184); txt('Período:', M + 10, y + 31);
  font('bold', 9); color(34, 197, 94); txt(period, M + 35, y + 31);

  font('normal', 8); color(148, 163, 184); txt('Generado:', M + 10, y + 40);
  font('normal', 8); color(148, 163, 184); txt(new Date().toLocaleString('es-EC'), M + 35, y + 40);

  font('normal', 8); color(148, 163, 184); txt('Alcance:', M + 10, y + 49);
  font('bold', 8); color(148, 163, 184); txt('Almacenamiento de Sustancias Químicas Peligrosas', M + 35, y + 49);

  // Signature line
  stroke(71, 85, 105); pdf.setDrawColor(71, 85, 105);
  line(M + 10, y + 62, M + 80, y + 62);
  font('normal', 7); color(71, 85, 105); txt('Responsable de Seguridad Química', M + 10, y + 67);

  line(pw - M - 70, y + 62, pw - M - 2, y + 62);
  txt('Fecha', pw - M - 70, y + 67);

  y += 76;

  // StorSafe seal
  checkPage(30, 'Firma');
  fill(22, 33, 51); rrect(pw / 2 - 55, y, 110, 25, 3);
  fill(34, 197, 94); rrect(pw / 2 - 55, y, 110, 1.5, 1);
  font('bold', 10); color(34, 197, 94); txt('StorSafe', pw / 2, y + 10, { align: 'center' });
  font('normal', 7); color(100, 116, 139);
  txt('Sistema de Gestión de Bodegas Químicas', pw / 2, y + 16, { align: 'center' });
  txt('Reporte generado automáticamente', pw / 2, y + 21, { align: 'center' });

  addFooter(pageNum);

  // ── Save ──────────────────────────────────────────────────────────────────

  const filename = `StorSafe_Reporte_${companyName.replace(/\s+/g, '_')}_${period.replace(/\s+/g, '_')}.pdf`;
  pdf.save(filename);
}

// ── Excel Export ──────────────────────────────────────────────────────────────

interface ExcelArgs {
  companyName: string;
  period: string;
  data: ReportData;
  incompatiblePairs: { a: Product; b: Product }[];
  segregarPairs: { a: Product; b: Product }[];
  zonesWithAlerts: Zone[];
  compliance: ComplianceScoreResult;
  isSuperAdmin: boolean;
}

function exportToExcel(args: ExcelArgs) {
  const { companyName, period, data, incompatiblePairs, segregarPairs, zonesWithAlerts, compliance, isSuperAdmin } = args;

  const wb = XLSX.utils.book_new();

  // ── Hoja 1: Resumen Ejecutivo ──────────────────────────────────────────────
  const sdsOk  = data.products.filter(p => p.sds_url).length;
  const sdsPct = data.products.length ? Math.round((sdsOk / data.products.length) * 100) : 0;

  const resumenRows = [
    { Indicador: 'Empresa', Valor: companyName },
    { Indicador: 'Período', Valor: period },
    { Indicador: 'Fecha de generación', Valor: new Date().toLocaleDateString('es-EC') },
    { Indicador: 'Total productos', Valor: data.products.length },
    { Indicador: 'Bodegas activas', Valor: data.warehouses.length },
    { Indicador: 'Zonas registradas', Valor: data.zones.length },
    { Indicador: 'Zonas con alertas', Valor: zonesWithAlerts.length },
    { Indicador: 'Hojas SDS disponibles', Valor: `${sdsOk}/${data.products.length} (${sdsPct}%)` },
    { Indicador: 'Pares incompatibles', Valor: incompatiblePairs.length },
    { Indicador: 'Pares a segregar', Valor: segregarPairs.length },
  ];
  XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(resumenRows), 'Resumen Ejecutivo');

  // ── Hoja 2: Inventario de Productos ──────────────────────────────────────
  const inventoryRows = data.products.map(p => ({
    Nombre:       p.name,
    'N° CAS':     p.cas_number ?? '',
    Proveedor:    p.provider ?? '',
    'Clases SGA': p.sga_classes.join(', '),
    'Hoja SDS':   p.sds_url ? 'Disponible' : 'Faltante',
  }));
  XLSX.utils.book_append_sheet(
    wb,
    XLSX.utils.json_to_sheet(inventoryRows.length ? inventoryRows : [{ Nombre: 'Sin productos registrados' }]),
    'Inventario de Productos',
  );

  // ── Hoja 3: Compatibilidad ────────────────────────────────────────────────
  const compatRows = [
    ...incompatiblePairs.map(pair => ({
      Tipo: 'Incompatible', 'Producto A': pair.a.name, 'Producto B': pair.b.name,
      'Clases A': pair.a.sga_classes.join(', '), 'Clases B': pair.b.sga_classes.join(', '),
    })),
    ...segregarPairs.map(pair => ({
      Tipo: 'A segregar', 'Producto A': pair.a.name, 'Producto B': pair.b.name,
      'Clases A': pair.a.sga_classes.join(', '), 'Clases B': pair.b.sga_classes.join(', '),
    })),
  ];
  XLSX.utils.book_append_sheet(
    wb,
    XLSX.utils.json_to_sheet(compatRows.length ? compatRows : [{ Tipo: 'Sin incompatibilidades detectadas' }]),
    'Compatibilidad',
  );

  // ── Hoja 4: Métricas SSO (solo superadmin) ────────────────────────────────
  // El breakdown solo está disponible una vez resuelto useComplianceScore —
  // si aún está cargando llega vacío y se omite la hoja.
  if (isSuperAdmin && compliance.breakdown.length > 0) {
    const ssoRows = compliance.breakdown.map(c => ({
      Categoría:           c.label,
      'Peso (%)':          c.weight,
      'Cumplimiento (%)':  Math.round(c.pct),
      Aplica:              c.applicable ? 'Sí' : 'No',
    }));
    ssoRows.push({ Categoría: 'SCORE TOTAL', 'Peso (%)': 100, 'Cumplimiento (%)': compliance.score, Aplica: 'Sí' });
    XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(ssoRows), 'Métricas SSO');
  }

  const filename = `StorSafe_Reporte_${companyName.replace(/\s+/g, '_')}_${period.replace(/\s+/g, '_')}.xlsx`;
  XLSX.writeFile(wb, filename);
}

// ── Utils ─────────────────────────────────────────────────────────────────────

function truncate(s: string, n: number) { return s.length > n ? s.slice(0, n - 1) + '…' : s; }

function hexToRgb(hex: string): [number, number, number] {
  const h = hex.replace('#', '');
  return [parseInt(h.slice(0, 2), 16), parseInt(h.slice(2, 4), 16), parseInt(h.slice(4, 6), 16)];
}

// ── Component ─────────────────────────────────────────────────────────────────

export default function Reports() {
  const { company, profile } = useCompany();
  const isViewer = profile?.role === 'viewer';
  const isSuperAdmin = profile?.role === 'superadmin';
  const { impersonated } = useImpersonation();
  const effectiveCompany = impersonated ?? company;

  const now = new Date();
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [year, setYear] = useState(now.getFullYear());
  const [data, setData] = useState<ReportData | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [generating, setGenerating] = useState(false);
  const [exportingExcel, setExportingExcel] = useState(false);
  const [logoImg, setLogoImg] = useState<HTMLImageElement | null>(null);
  const complianceResult = useComplianceScore(effectiveCompany?.id);

  useEffect(() => {
    const img = new Image();
    img.onload = () => setLogoImg(img);
    img.src = logoSrc;
  }, []);
  const [history, setHistory] = useState<HistoryEntry[]>([]);
  const [activeSection, setActiveSection] = useState<'preview' | 'history'>('preview');

  const loadHistory = useCallback(async () => {
    if (!effectiveCompany) { setHistory([]); return; }
    const { data, error } = await supabase
      .from('report_history')
      .select('id, company_id, company_name, period, generated_at')
      .eq('company_id', effectiveCompany.id)
      .order('generated_at', { ascending: false })
      .limit(30);
    if (error) { console.error('Error al cargar historial de reportes:', error.message); return; }
    setHistory((data ?? []) as HistoryEntry[]);
  }, [effectiveCompany]);

  useEffect(() => { loadHistory(); }, [loadHistory]);

  const saveHistory = useCallback(async (entry: { company_id: string; company_name: string; period: string }) => {
    const { data: userData } = await supabase.auth.getUser();
    const { data, error } = await supabase
      .from('report_history')
      .insert({
        company_id:   entry.company_id,
        company_name: entry.company_name,
        period:       entry.period,
        generated_by: userData.user?.id ?? null,
      })
      .select('id, company_id, company_name, period, generated_at')
      .single();
    if (error) { console.error('Error al guardar historial de reporte:', error.message); return; }
    setHistory(prev => [data as HistoryEntry, ...prev].slice(0, 30));
  }, []);

  const fetchData = useCallback(async () => {
    if (!effectiveCompany) return;
    setLoading(true);
    setError('');
    const cid = effectiveCompany.id;
    const [
      { data: products,   error: err1 },
      { data: warehouses, error: err2 },
      { data: zones,       error: err3 },
    ] = await Promise.all([
      supabase.from('products').select('id,name,cas_number,provider,sga_classes,sds_url').eq('company_id', cid).order('name'),
      supabase.from('warehouses').select('id,name,description').eq('company_id', cid).order('name'),
      // warehouse_zones no tiene company_id: se filtra por la empresa de su bodega.
      supabase.from('warehouse_zones').select('id,warehouse_id,name,product_ids,warehouses!inner(company_id)').eq('warehouses.company_id', cid),
    ]);
    if (err1 || err2 || err3) {
      setError('Error al cargar los datos. Por favor recarga la página.');
      setLoading(false);
      return;
    }
    setData({ products: products ?? [], warehouses: warehouses ?? [], zones: zones ?? [] });
    setLoading(false);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [effectiveCompany]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const periodLabel = `${MONTHS[month - 1]} ${year}`;

  // Derived
  const productMap = new Map((data?.products ?? []).map(p => [p.id, p]));
  const prods = data?.products ?? [];
  const incompatiblePairs: { a: Product; b: Product }[] = [];
  const segregarPairs: { a: Product; b: Product }[] = [];

  for (let i = 0; i < prods.length; i++) {
    for (let j = i + 1; j < prods.length; j++) {
      const lvl = productCompat(prods[i], prods[j]);
      if (lvl === 'incompatible') incompatiblePairs.push({ a: prods[i], b: prods[j] });
      else if (lvl === 'segregar') segregarPairs.push({ a: prods[i], b: prods[j] });
    }
  }

  const zonesWithAlerts = (data?.zones ?? []).filter(z => zoneHasAlert(z, productMap));
  const byClass: Record<string, Product[]> = {};
  for (const p of prods) {
    for (const c of p.sga_classes) {
      if (!byClass[c]) byClass[c] = [];
      byClass[c].push(p);
    }
  }

  const withSds    = prods.filter(p => p.sds_url).length;
  const missingSds = prods.filter(p => !p.sds_url);
  const sdsPct     = prods.length ? Math.round((withSds / prods.length) * 100) : 0;

  const handleGenerate = async () => {
    if (!effectiveCompany || !data) return;
    setGenerating(true);
    try {
      generatePdf({
        companyName: effectiveCompany.name,
        period: `Generado el ${new Date().toLocaleDateString('es-EC', { day: '2-digit', month: 'long', year: 'numeric' })} — Estado actual del inventario`,
        data,
        productMap,
        incompatiblePairs,
        segregarPairs,
        zonesWithAlerts,
        byClass,
        logoImg,
      });
      await saveHistory({
        period:       periodLabel,
        company_id:   effectiveCompany.id,
        company_name: effectiveCompany.name,
      });
    } finally {
      setGenerating(false);
    }
  };

  const handleExportExcel = () => {
    if (!effectiveCompany || !data) return;
    setExportingExcel(true);
    try {
      exportToExcel({
        companyName: effectiveCompany.name,
        period: `Generado el ${new Date().toLocaleDateString('es-EC', { day: '2-digit', month: 'long', year: 'numeric' })} — Estado actual del inventario`,
        data,
        incompatiblePairs,
        segregarPairs,
        zonesWithAlerts,
        compliance: complianceResult,
        isSuperAdmin,
      });
    } finally {
      setExportingExcel(false);
    }
  };

  const currentYear = now.getFullYear();
  const years = Array.from({ length: 5 }, (_, i) => currentYear - i);

  return (
    <div className="rpt-root">
      {/* ── Controls bar ── */}
      <div className="rpt-controls">
        <div className="rpt-controls-left">
          <div className="rpt-period-group">
            <label className="rpt-label">Período</label>
            <div className="rpt-period-selectors">
              <select
                className="rpt-select"
                value={month}
                onChange={e => setMonth(Number(e.target.value))}
              >
                {MONTHS.map((m, i) => (
                  <option key={i + 1} value={i + 1}>{m}</option>
                ))}
              </select>
              <select
                className="rpt-select"
                value={year}
                onChange={e => setYear(Number(e.target.value))}
              >
                {years.map(y => <option key={y} value={y}>{y}</option>)}
              </select>
            </div>
            <p style={{ fontSize: '0.72rem', color: '#64748b', margin: '2px 0 0', maxWidth: 260 }}>
              Este reporte refleja el estado actual del inventario al momento de su
              generación. Para consultar reportes anteriores, revisa el historial de
              reportes generados.
            </p>
          </div>

          <div className="rpt-period-badge">
            <ReportIcon />
            <span>Reporte: <strong>{periodLabel}</strong></span>
          </div>
        </div>

        <div className="rpt-controls-right">
          {isViewer ? (
            <p style={{ fontSize: '0.82rem', color: '#64748b', margin: 0 }}>
              Solo administradores y gestores pueden generar reportes.
            </p>
          ) : (
            <>
              <button
                className="rpt-btn-download"
                onClick={handleGenerate}
                disabled={generating || loading || !data}
              >
                {generating ? (
                  <><span className="rpt-spinner" /> Generando…</>
                ) : (
                  <><DownloadIcon /> Descargar PDF</>
                )}
              </button>
              {data && (
                <button
                  className="rpt-btn-download rpt-btn-excel"
                  onClick={handleExportExcel}
                  disabled={exportingExcel || loading}
                >
                  {exportingExcel ? (
                    <><span className="rpt-spinner" /> Exportando…</>
                  ) : (
                    <><DownloadIcon /> Exportar Excel</>
                  )}
                </button>
              )}
            </>
          )}
        </div>
      </div>

      {/* ── Tabs ── */}
      <div className="rpt-tabs">
        <button
          className={`rpt-tab ${activeSection === 'preview' ? 'active' : ''}`}
          onClick={() => setActiveSection('preview')}
        >
          <EyeIcon /> Vista previa
        </button>
        <button
          className={`rpt-tab ${activeSection === 'history' ? 'active' : ''}`}
          onClick={() => setActiveSection('history')}
        >
          <ClockIcon /> Historial {history.length > 0 && <span className="rpt-tab-badge">{history.length}</span>}
        </button>
      </div>

      {/* ── Preview ── */}
      {activeSection === 'preview' && (
        <div className="rpt-preview">
          {error ? (
            <div className="rpt-empty">
              <p>{error}</p>
              <button className="rpt-btn-download" onClick={fetchData} style={{ marginTop: 12 }}>Reintentar</button>
            </div>
          ) : loading ? (
            <div className="rpt-loading">
              <span className="rpt-spinner large" />
              <p>Cargando datos…</p>
            </div>
          ) : !data ? (
            <div className="rpt-empty">Sin datos disponibles</div>
          ) : (
            <>
              {/* Cover preview */}
              <div className="rpt-cover-preview">
                <div className="rpt-cover-logo">
                  <StorSafeIcon />
                </div>
                <div className="rpt-cover-info">
                  <div className="rpt-cover-title">REPORTE DE CUMPLIMIENTO</div>
                  <div className="rpt-cover-norm">NTE INEN 2266:2013 · Sistema de Gestión de Bodegas Químicas</div>
                  <div className="rpt-cover-meta">
                    <span className="rpt-cover-company">{effectiveCompany?.name}</span>
                    <span className="rpt-cover-period">{periodLabel}</span>
                    <span className="rpt-cover-date">
                      Generado: {new Date().toLocaleDateString('es-EC', { day:'numeric', month:'long', year:'numeric' })}
                    </span>
                  </div>
                </div>
              </div>

              {/* Stat cards */}
              <div className="rpt-stats-grid">
                <div className="rpt-stat-card green">
                  <div className="rpt-stat-icon"><BoxIcon /></div>
                  <div className="rpt-stat-body">
                    <div className="rpt-stat-value">{data.products.length}</div>
                    <div className="rpt-stat-label">Total Productos</div>
                  </div>
                </div>
                <div className="rpt-stat-card blue">
                  <div className="rpt-stat-icon"><WarehouseIcon /></div>
                  <div className="rpt-stat-body">
                    <div className="rpt-stat-value">{data.warehouses.length}</div>
                    <div className="rpt-stat-label">Bodegas</div>
                  </div>
                </div>
                <div className="rpt-stat-card purple">
                  <div className="rpt-stat-icon"><ZoneIcon /></div>
                  <div className="rpt-stat-body">
                    <div className="rpt-stat-value">{data.zones.length}</div>
                    <div className="rpt-stat-label">Zonas</div>
                  </div>
                </div>
                <div className="rpt-stat-card red">
                  <div className="rpt-stat-icon"><AlertIcon /></div>
                  <div className="rpt-stat-body">
                    <div className="rpt-stat-value">{incompatiblePairs.length}</div>
                    <div className="rpt-stat-label">Pares Incompatibles</div>
                  </div>
                </div>
              </div>

              {/* SDS status */}
              <div className="rpt-section">
                <div className="rpt-section-header">
                  <DocumentIcon />
                  <h3>Hojas de Seguridad SDS</h3>
                  <span className={`rpt-badge ${sdsPct >= 80 ? 'green' : sdsPct >= 50 ? 'yellow' : 'red'}`}>{sdsPct}%</span>
                </div>
                <div className="rpt-sds-bar-wrap">
                  <div className="rpt-sds-bar-track">
                    <div
                      className="rpt-sds-bar-fill"
                      style={{ width: `${sdsPct}%`, background: sdsPct >= 80 ? '#22c55e' : sdsPct >= 50 ? '#f59e0b' : '#ef4444' }}
                    />
                  </div>
                  <span className="rpt-sds-bar-label">{withSds} de {data.products.length}</span>
                </div>
                {missingSds.length > 0 && (
                  <div className="rpt-sds-missing">
                    <div className="rpt-sds-missing-header">
                      <span className="rpt-badge red">{missingSds.length} faltante{missingSds.length !== 1 ? 's' : ''}</span>
                    </div>
                    <div className="rpt-sds-chips">
                      {missingSds.slice(0, 8).map(p => (
                        <span key={p.id} className="rpt-chip red">{p.name}</span>
                      ))}
                      {missingSds.length > 8 && (
                        <span className="rpt-chip gray">+{missingSds.length - 8} más</span>
                      )}
                    </div>
                  </div>
                )}
              </div>

              {/* Compatibility summary */}
              <div className="rpt-section">
                <div className="rpt-section-header">
                  <GridIcon />
                  <h3>Compatibilidad Química</h3>
                </div>
                <div className="rpt-compat-summary">
                  <div className={`rpt-compat-block ${incompatiblePairs.length > 0 ? 'red' : 'green'}`}>
                    <div className="rpt-compat-count">{incompatiblePairs.length}</div>
                    <div className="rpt-compat-label">Pares Incompatibles</div>
                    <div className="rpt-compat-hint">No almacenar juntos</div>
                  </div>
                  <div className={`rpt-compat-block ${segregarPairs.length > 0 ? 'yellow' : 'green'}`}>
                    <div className="rpt-compat-count">{segregarPairs.length}</div>
                    <div className="rpt-compat-label">Pares a Segregar</div>
                    <div className="rpt-compat-hint">Requieren separación</div>
                  </div>
                  <div className={`rpt-compat-block ${zonesWithAlerts.length > 0 ? 'red' : 'green'}`}>
                    <div className="rpt-compat-count">{zonesWithAlerts.length}</div>
                    <div className="rpt-compat-label">Zonas con Alertas</div>
                    <div className="rpt-compat-hint">Revisión requerida</div>
                  </div>
                </div>

                {incompatiblePairs.length > 0 && (
                  <div className="rpt-pairs-list">
                    <div className="rpt-pairs-title red">Incompatibilidades detectadas</div>
                    {incompatiblePairs.slice(0, 6).map((pair, i) => (
                      <div key={i} className="rpt-pair-row red">
                        <span className="rpt-pair-name">{pair.a.name}</span>
                        <span className="rpt-pair-sep">×</span>
                        <span className="rpt-pair-name">{pair.b.name}</span>
                      </div>
                    ))}
                    {incompatiblePairs.length > 6 && (
                      <div className="rpt-pairs-more">+{incompatiblePairs.length - 6} más en el PDF</div>
                    )}
                  </div>
                )}

                {segregarPairs.length > 0 && (
                  <div className="rpt-pairs-list" style={{ marginTop: 8 }}>
                    <div className="rpt-pairs-title yellow">A segregar</div>
                    {segregarPairs.slice(0, 4).map((pair, i) => (
                      <div key={i} className="rpt-pair-row yellow">
                        <span className="rpt-pair-name">{pair.a.name}</span>
                        <span className="rpt-pair-sep">↔</span>
                        <span className="rpt-pair-name">{pair.b.name}</span>
                      </div>
                    ))}
                    {segregarPairs.length > 4 && (
                      <div className="rpt-pairs-more">+{segregarPairs.length - 4} más en el PDF</div>
                    )}
                  </div>
                )}
              </div>

              {/* Product classes */}
              <div className="rpt-section">
                <div className="rpt-section-header">
                  <BoxIcon />
                  <h3>Distribución por Clase SGA</h3>
                </div>
                <div className="rpt-class-grid">
                  {SGA_CLASSES.filter(c => byClass[c.id]?.length > 0).map(cls => (
                    <div key={cls.id} className="rpt-class-chip" style={{ borderColor: cls.color + '60', background: cls.bg }}>
                      <span className="rpt-class-dot" style={{ background: cls.color }} />
                      <span className="rpt-class-label" style={{ color: cls.color }}>{cls.label}</span>
                      <span className="rpt-class-count" style={{ color: cls.color }}>{byClass[cls.id].length}</span>
                    </div>
                  ))}
                  {SGA_CLASSES.every(c => !byClass[c.id]?.length) && (
                    <div className="rpt-empty-small">Sin productos con clase SGA asignada</div>
                  )}
                </div>
              </div>

              {/* Warehouses */}
              <div className="rpt-section">
                <div className="rpt-section-header">
                  <WarehouseIcon />
                  <h3>Estado de Bodegas</h3>
                </div>
                {data.warehouses.length === 0 ? (
                  <div className="rpt-empty-small">Sin bodegas registradas</div>
                ) : (
                  <div className="rpt-wh-list">
                    {data.warehouses.map(wh => {
                      const whZones = data.zones.filter(z => z.warehouse_id === wh.id);
                      const alerts  = whZones.filter(z => zoneHasAlert(z, productMap)).length;
                      return (
                        <div key={wh.id} className={`rpt-wh-row ${alerts > 0 ? 'alert' : ''}`}>
                          <div className="rpt-wh-info">
                            <span className="rpt-wh-name">{wh.name}</span>
                            {wh.description && <span className="rpt-wh-desc">{wh.description}</span>}
                          </div>
                          <div className="rpt-wh-meta">
                            <span className="rpt-wh-zones">{whZones.length} zona{whZones.length !== 1 ? 's' : ''}</span>
                            {alerts > 0
                              ? <span className="rpt-badge red">{alerts} alerta{alerts !== 1 ? 's' : ''}</span>
                              : <span className="rpt-badge green">Sin alertas</span>
                            }
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>

              {/* Footer notice */}
              <div className="rpt-footer-notice">
                <StorSafeIcon />
                <div>
                  <div className="rpt-footer-title">StorSafe · Sistema de Gestión de Bodegas Químicas</div>
                  <div className="rpt-footer-sub">El PDF incluye todas las secciones completas con tablas detalladas y análisis de incompatibilidades en almacenamiento según la NTE INEN 2266:2013, 6.1.7.10 b)</div>
                </div>
              </div>
            </>
          )}
        </div>
      )}

      {/* ── History ── */}
      {activeSection === 'history' && (
        <div className="rpt-preview">
          {history.length === 0 ? (
            <div className="rpt-empty">
              <ClockIcon />
              <p>No hay reportes generados aún</p>
              <p className="rpt-empty-sub">Los reportes descargados aparecerán aquí</p>
            </div>
          ) : (
            <div className="rpt-history-list">
              <div className="rpt-history-header">
                <h3>Reportes generados — {effectiveCompany?.name}</h3>
                <span className="rpt-badge gray">{history.length} total</span>
              </div>
              {history.map(entry => (
                <div key={entry.id} className="rpt-history-row">
                  <div className="rpt-history-icon">
                    <PdfIcon />
                  </div>
                  <div className="rpt-history-info">
                    <div className="rpt-history-period">{entry.period}</div>
                    <div className="rpt-history-company">{entry.company_name}</div>
                    <div className="rpt-history-date">
                      {new Date(entry.generated_at).toLocaleString('es-EC', {
                        day: 'numeric', month: 'long', year: 'numeric',
                        hour: '2-digit', minute: '2-digit',
                      })}
                    </div>
                  </div>
                  <span className="rpt-badge green">PDF</span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ── Icons ─────────────────────────────────────────────────────────────────────

function StorSafeIcon() {
  return (
    <svg width="28" height="28" viewBox="0 0 48 48" fill="none">
      <rect width="48" height="48" rx="12" fill="#22c55e" />
      <path d="M14 18h20v14a2 2 0 01-2 2H16a2 2 0 01-2-2V18z" fill="white" opacity="0.9" />
      <path d="M12 16a2 2 0 012-2h20a2 2 0 012 2v2H12v-2z" fill="white" />
      <circle cx="24" cy="27" r="3" fill="#22c55e" />
      <rect x="23" y="27" width="2" height="4" rx="1" fill="#22c55e" />
    </svg>
  );
}

function ReportIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z"/>
      <polyline points="14 2 14 8 20 8"/>
      <line x1="16" y1="13" x2="8" y2="13"/>
      <line x1="16" y1="17" x2="8" y2="17"/>
      <polyline points="10 9 9 9 8 9"/>
    </svg>
  );
}

function DownloadIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4"/>
      <polyline points="7 10 12 15 17 10"/>
      <line x1="12" y1="15" x2="12" y2="3"/>
    </svg>
  );
}

function EyeIcon() {
  return (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/>
    </svg>
  );
}

function ClockIcon() {
  return (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/>
    </svg>
  );
}

function BoxIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
      <path d="M21 16V8a2 2 0 00-1-1.73l-7-4a2 2 0 00-2 0l-7 4A2 2 0 003 8v8a2 2 0 001 1.73l7 4a2 2 0 002 0l7-4A2 2 0 0021 16z"/>
      <polyline points="3.27 6.96 12 12.01 20.73 6.96"/>
      <line x1="12" y1="22.08" x2="12" y2="12"/>
    </svg>
  );
}

function WarehouseIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
      <path d="M3 9l9-7 9 7v11a2 2 0 01-2 2H5a2 2 0 01-2-2z"/>
      <polyline points="9 22 9 12 15 12 15 22"/>
    </svg>
  );
}

function ZoneIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
      <rect x="3" y="3" width="7" height="7" rx="1"/>
      <rect x="14" y="3" width="7" height="7" rx="1"/>
      <rect x="3" y="14" width="7" height="7" rx="1"/>
      <rect x="14" y="14" width="7" height="7" rx="1"/>
    </svg>
  );
}

function AlertIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
      <path d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z"/>
      <line x1="12" y1="9" x2="12" y2="13"/>
      <line x1="12" y1="17" x2="12.01" y2="17"/>
    </svg>
  );
}

function DocumentIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z"/>
      <polyline points="14 2 14 8 20 8"/>
    </svg>
  );
}

function GridIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <rect x="3" y="3" width="18" height="18" rx="2"/>
      <line x1="3" y1="9" x2="21" y2="9"/>
      <line x1="3" y1="15" x2="21" y2="15"/>
      <line x1="9" y1="3" x2="9" y2="21"/>
      <line x1="15" y1="3" x2="15" y2="21"/>
    </svg>
  );
}

function PdfIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
      <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z"/>
      <polyline points="14 2 14 8 20 8"/>
      <line x1="16" y1="13" x2="8" y2="13"/>
      <line x1="16" y1="17" x2="8" y2="17"/>
      <line x1="10" y1="9" x2="8" y2="9"/>
    </svg>
  );
}
